import styled from 'styled-components';

import Tooltip from 'components/shared/Tooltip';
import { NetworkStatus, PAYER_TYPES } from 'models/GroupPayerNetworkStatus';
import { GroupPayerNetworkStatusOnAction } from 'models/WorkflowAction';
import { colors } from 'styles/theme/colors';
import InNetworkIcon from 'svg/InNetworkIcon';
import OutOfNetworkIcon from 'svg/OutOfNetworkIcon';

export function NetworkStatusLink({
  facilityName,
  status,
}: {
  facilityName: string;
  status: GroupPayerNetworkStatusOnAction;
}) {
  if (!status.configured) return <span>No Network Status Configured</span>;

  return (
    <Tooltip
      text={<NetworkStatusSummary facilityName={facilityName} status={status} />}
      position='bottom'
      card
      maxWidth={360}>
      <LinkButton type='button' data-testid='view-network-status'>
        View Network Status
      </LinkButton>
    </Tooltip>
  );
}

function NetworkStatusSummary({
  facilityName,
  status,
}: {
  facilityName: string;
  status: GroupPayerNetworkStatusOnAction;
}) {
  return (
    <Summary data-testid='network-status-tooltip'>
      <SummaryHeader>
        <SummaryTitle>{facilityName}</SummaryTitle>
        {status.stediPayerName && (
          <SummaryPayer data-testid='network-status-tooltip-payer'>{status.stediPayerName}</SummaryPayer>
        )}
      </SummaryHeader>
      <SummaryRows>
        {PAYER_TYPES.map(({ field, label }) => (
          <SummaryRow key={field} data-testid={`network-status-tooltip-${field}`}>
            <StatusIcon value={status[field]} />
            <span>
              <SummaryPayerType>{label}</SummaryPayerType> ({STATUS_LABELS[status[field] ?? 'unset']})
            </span>
          </SummaryRow>
        ))}
      </SummaryRows>
    </Summary>
  );
}

const STATUS_LABELS = { in: 'In Network', out: 'Out of Network', unset: 'Not Set' };

function StatusIcon({ value }: { value: NetworkStatus }) {
  if (value === 'in') return <InNetworkIcon />;
  if (value === 'out') return <OutOfNetworkIcon />;
  return <UnsetIcon />;
}

const LinkButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: ${colors.blue100};
  font: inherit;
  text-decoration: underline;
  cursor: default;
`;

const Summary = styled.div`
  line-height: 18px;
`;

const SummaryHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 20px 12px;
  border-bottom: 1px solid ${colors.white25};
`;

const SummaryPayer = styled.span`
  color: ${colors.white75};
`;

const SummaryTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
`;

const SummaryRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 20px;
`;

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  white-space: nowrap;
`;

const SummaryPayerType = styled.span`
  font-weight: 500;
`;

const UnsetIcon = styled.span`
  flex-shrink: 0;
  box-sizing: border-box;
  width: 12px;
  height: 12px;
  border: 1px solid ${colors.white25};
  border-radius: 50%;
`;
